// Global Instructions Rule Applied!
import React from 'react'
import { List, Avatar, Tag } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { NormalizedRow, RowStat } from '../lib/listDescriptors.ts'

/**
 * The row's secondary content: an optional subtitle line and an optional compact stats
 * line (key figures, `·`-spaced via flex gap, capped at 4). Returns null when there's
 * nothing to show, so Home recents (no stats) render exactly as before.
 */
function RowDescription({ subtitle, stats }: { subtitle?: string; stats?: RowStat[] }): React.JSX.Element | null {
  const shown = (stats ?? []).slice(0, 4)
  if (!subtitle && shown.length === 0) return null
  return (
    <span>
      {subtitle ? <span>{subtitle}</span> : null}
      {shown.length > 0 ? (
        <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', marginTop: subtitle ? 2 : 0 }}>
          {shown.map((s, i) => (
            <span key={i} style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {s.label ? <span style={{ color: 'rgba(0,0,0,0.4)' }}>{s.label} </span> : null}
              <span style={{ fontWeight: 500, color: 'rgba(0,0,0,0.7)' }}>{s.value}</span>
            </span>
          ))}
        </span>
      ) : null}
    </span>
  )
}

/**
 * A single tappable record row (avatar/initials + title + optional stage tag + subtitle +
 * stats + chevron). Shared by the browse lists and the Home recents list so they look identical.
 */
export function RecordRow({ item, onClick }: { item: NormalizedRow; onClick?: () => void }): React.JSX.Element {
  return (
    <List.Item
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        background: '#fff',
        borderRadius: 8,
        marginTop: 6,
        paddingLeft: 12,
        paddingRight: 12
      }}
    >
      <List.Item.Meta
        avatar={
          <Avatar src={item.avatarSrc || undefined} style={{ backgroundColor: '#1890ff', color: '#fff', fontSize: 12 }}>
            {item.avatarText || '??'}
          </Avatar>
        }
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</span>
            {item.tagText ? (
              <Tag
                color={item.tagColor || 'default'}
                style={item.tagColor ? { color: '#fff', borderColor: item.tagColor, margin: 0 } : { margin: 0 }}
              >
                {item.tagText}
              </Tag>
            ) : null}
          </span>
        }
        description={<RowDescription subtitle={item.subtitle} stats={item.stats} />}
      />
      {onClick ? <FontAwesomeIcon icon={faChevronRight} style={{ color: 'rgba(0,0,0,0.25)' }} /> : null}
    </List.Item>
  )
}

export default RecordRow
